import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';
import { generateTenantCertificatePDF, generateMietvertragPDF, generateHandoverPDF } from '../lib/pdf';
import LimitBanner from '../components/LimitBanner';
import { canCreateTenant, getTenantLimit, getUsageLabel } from '../lib/planLimits';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
};

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '', iban: '',
  lease_start: '', lease_end: '', monthly_rent: '', rent_due_day: '1', property_id: '', unit: '', unit_id: '',
};

const inputStyle = {
  width: '100%', border: `1px solid ${T.border}`,
  padding: '10px 12px', fontSize: 13, boxSizing: 'border-box',
  color: T.ink, background: 'white', outline: 'none',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function calcDueDate(year, month, rentDueDay) {
  if (!rentDueDay || rentDueDay === 'end_of_month') {
    return new Date(year, month, 0).toISOString().split('T')[0];
  }
  const last = new Date(year, month, 0).getDate();
  const day  = Math.min(parseInt(rentDueDay) || 1, last);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const PAYMENT_STATUS_CFG = {
  paid:    { label: 'Bezahlt',           cls: 'badge-green'  },
  pending: { label: 'Ausstehend',        cls: 'badge-yellow' },
  overdue: { label: 'Überfällig',        cls: 'badge-red'    },
  partial: { label: 'Teilweise',         cls: 'badge-blue'   },
};

function urgencyInfo(dueDate, status) {
  if (status === 'paid') return null;
  if (!dueDate) return { label: 'Offen', color: '#b45309', bg: '#fef3c7' };
  const days = Math.floor((new Date() - new Date(dueDate)) / 86400000);
  if (status === 'overdue' || days > 0) {
    if (days <= 0) return { label: 'Heute fällig', color: '#b45309', bg: '#fef3c7' };
    return { label: `${days} ${days === 1 ? 'Tag' : 'Tage'} überfällig`, color: '#dc2626', bg: '#fee2e2', critical: true };
  }
  // pending & not yet past due
  const due = new Date(dueDate);
  const dateStr = due.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  if (days === 0) return { label: `Heute fällig · ${dateStr}`, color: '#b45309', bg: '#fef3c7' };
  return { label: `Fällig am ${dateStr}`, color: '#555', bg: '#f5f5f5' };
}

// keep for backward-compat (TenantCard uses it)
function daysOverdueLabel(dueDate, status) {
  const info = urgencyInfo(dueDate, status);
  if (!info || !info.critical) return null;
  return info.label;
}

function leaseStatus(lease_start, lease_end) {
  if (!lease_start) return null;
  const today = new Date(), start = new Date(lease_start);
  const end   = lease_end ? new Date(lease_end) : null;
  if (start > today) return { label: 'Zukünftig', cls: 'badge-blue' };
  if (end && end < today) return { label: 'Beendet', cls: 'badge-gray' };
  if (end) {
    const daysLeft = Math.ceil((end - today) / 86400000);
    if (daysLeft <= 60) return { label: `Läuft ab · ${daysLeft}T`, cls: 'badge-yellow' };
  }
  return { label: 'Aktiv', cls: 'badge-green' };
}

/* ── Portal status badge ── */
function PortalBadge({ tenant }) {
  if (tenant.portal_enabled)      return <span className="badge badge-green" style={{ fontSize: 10 }}>Portal aktiv</span>;
  if (tenant.invitation_sent_at)  return <span className="badge badge-yellow" style={{ fontSize: 10 }}>Eingeladen</span>;
  return null;
}

/* ── Tenant row (desktop) ── */
function TenantRow({ tenant, propName, payment, onMarkPaid, onMarkOpen, onRemind, onEdit, onDelete, isMarking, last }) {
  const payStatus  = payment?.status || 'pending';
  const isPaid     = payStatus === 'paid';
  const isOverdue  = payStatus === 'overdue';
  const dueDate    = payment?.due_date || null;
  const leaseS     = leaseStatus(tenant.lease_start, tenant.lease_end);
  const urgency    = urgencyInfo(dueDate, payStatus);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1.8fr 1.3fr 1fr 1.4fr auto',
      padding: '14px 20px 14px 0', alignItems: 'center',
      borderBottom: last ? 'none' : `1px solid ${T.border}`,
      borderLeft: urgency?.critical ? '4px solid #ef4444' : isOverdue ? '4px solid #f97316' : '4px solid transparent',
      paddingLeft: 20,
      transition: 'background 0.12s',
      background: urgency?.critical ? 'rgba(254,242,242,0.35)' : 'transparent',
    }}
      onMouseEnter={e => e.currentTarget.style.background = urgency?.critical ? 'rgba(254,242,242,0.6)' : '#fafafa'}
      onMouseLeave={e => e.currentTarget.style.background = urgency?.critical ? 'rgba(254,242,242,0.35)' : 'transparent'}
    >
      {/* Mieter */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
          {tenant.first_name} {tenant.last_name}
        </div>
        {tenant.email && (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{tenant.email}</div>
        )}
        {leaseS && leaseS.label !== 'Aktiv' && (
          <span className={`badge ${leaseS.cls}`} style={{ fontSize: 10, marginTop: 4, display: 'inline-block' }}>
            {leaseS.label}
          </span>
        )}
      </div>

      {/* Objekt */}
      <div>
        {propName
          ? <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{propName}</div>
          : <div style={{ fontSize: 13, color: T.muted }}>–</div>}
        {tenant.unit && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{tenant.unit}</div>}
      </div>

      {/* Miete */}
      <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, letterSpacing: '-0.3px' }}>
        {tenant.monthly_rent ? `${Number(tenant.monthly_rent).toLocaleString('de-DE')} €` : '–'}
      </div>

      {/* Dringlichkeit — ersetzt "Fällig" + "Status" */}
      <div>
        {isPaid ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>Bezahlt</span>
          </div>
        ) : urgency ? (
          <span style={{
            display: 'inline-block', fontSize: 12, fontWeight: 700,
            color: urgency.color, background: urgency.bg,
            borderRadius: 100, padding: '3px 10px', whiteSpace: 'nowrap',
          }}>
            {urgency.label}
          </span>
        ) : null}
      </div>

      {/* Aktionen */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
        {!isPaid && (
          <button
            onClick={() => onRemind(payment, null, tenant)}
            style={{
              fontSize: 12, height: 36, padding: '0 12px', whiteSpace: 'nowrap',
              cursor: 'pointer', fontWeight: 700,
              background: 'white', border: `1.5px solid ${T.ink}`,
              borderRadius: 8, color: T.ink,
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.ink; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = T.ink; }}
          >
            Mahnung senden
          </button>
        )}
        {!isPaid && (
          <button
            onClick={() => onMarkPaid(payment, tenant)}
            disabled={!!isMarking}
            style={{
              fontSize: 12, height: 36, padding: '0 12px', whiteSpace: 'nowrap',
              cursor: isMarking ? 'wait' : 'pointer', fontWeight: 700,
              background: T.ink, border: 'none', borderRadius: 8, color: 'white',
              opacity: isMarking ? 0.5 : 1, transition: 'background 0.12s',
            }}
            onMouseEnter={e => { if (!isMarking) e.currentTarget.style.background = '#333'; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.ink; }}
          >
            {isMarking ? '…' : 'Als bezahlt markieren'}
          </button>
        )}
        {isPaid && payment && (
          <button
            onClick={() => onMarkOpen(payment)}
            disabled={!!isMarking}
            style={{
              fontSize: 11, height: 30, padding: '0 10px', whiteSpace: 'nowrap',
              cursor: isMarking ? 'wait' : 'pointer', fontWeight: 600,
              background: 'white', border: `1px solid ${T.border}`, borderRadius: 8, color: '#b45309',
              opacity: isMarking ? 0.5 : 1,
            }}
          >
            Als offen markieren
          </button>
        )}
        <button onClick={() => onEdit(tenant)} className="btn-ghost" style={{ fontSize: 11, height: 30, padding: '0 10px' }}>Bearb.</button>
        <button onClick={() => onDelete(tenant.id)} style={{ fontSize: 11, height: 30, padding: '0 10px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#dc2626', cursor: 'pointer' }}>Löschen</button>
      </div>
    </div>
  );
}

/* ── Tenant card (mobile) ── */
function TenantCard({ tenant, propName, payment, onMarkPaid, onMarkOpen, onRemind, onEdit, onDelete, isMarking }) {
  const payStatus = payment?.status || 'pending';
  const isPaid    = payStatus === 'paid';
  const dueDate   = payment?.due_date || null;
  const urgency   = urgencyInfo(dueDate, payStatus);

  return (
    <div style={{
      background: urgency?.critical ? '#fff5f5' : 'white',
      border: `1.5px solid ${urgency?.critical ? '#fecaca' : T.border}`,
      borderLeft: `4px solid ${urgency?.critical ? '#ef4444' : urgency && !isPaid ? '#f59e0b' : T.border}`,
      borderRadius: 14, padding: '16px 18px',
      boxShadow: urgency?.critical ? '0 2px 8px rgba(220,38,38,0.06)' : '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      {/* Top: name + urgency badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
            {tenant.first_name} {tenant.last_name}
          </div>
          {propName && (
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              {propName}{tenant.unit ? ` · ${tenant.unit}` : ''}
            </div>
          )}
        </div>
        {isPaid ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 8 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>Bezahlt</span>
          </div>
        ) : urgency ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: urgency.color, background: urgency.bg, borderRadius: 100, padding: '3px 10px', flexShrink: 0, marginLeft: 8, whiteSpace: 'nowrap' }}>
            {urgency.label}
          </span>
        ) : null}
      </div>

      {/* Amount */}
      {tenant.monthly_rent && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: T.muted, marginBottom: 3 }}>Miete / Monat</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.ink, letterSpacing: '-0.8px' }}>
            {Number(tenant.monthly_rent).toLocaleString('de-DE')} €
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!isPaid && (
          <button
            onClick={() => onMarkPaid(payment, tenant)}
            disabled={!!isMarking}
            style={{
              fontSize: 14, height: 44, fontWeight: 700,
              background: T.ink, border: 'none', borderRadius: 9,
              color: 'white', cursor: isMarking ? 'wait' : 'pointer',
              opacity: isMarking ? 0.5 : 1,
            }}
          >
            {isMarking ? '…' : 'Als bezahlt markieren'}
          </button>
        )}
        {isPaid && payment && (
          <button
            onClick={() => onMarkOpen(payment)}
            disabled={!!isMarking}
            style={{
              fontSize: 13, height: 42, fontWeight: 700,
              background: 'white', border: `1.5px solid #b45309`,
              borderRadius: 9, color: '#b45309',
              cursor: isMarking ? 'wait' : 'pointer', opacity: isMarking ? 0.5 : 1,
            }}
          >
            Als offen markieren
          </button>
        )}
        {!isPaid && (
          <button
            onClick={() => onRemind(payment, null, tenant)}
            style={{
              fontSize: 13, height: 42, fontWeight: 700,
              background: 'white', border: `1.5px solid ${T.ink}`,
              borderRadius: 9, color: T.ink, cursor: 'pointer',
            }}
          >
            Mahnung senden
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(tenant)} className="btn-ghost" style={{ fontSize: 12, height: 38, padding: '0 14px', flex: 1 }}>Bearbeiten</button>
          <button onClick={() => onDelete(tenant.id)} style={{ fontSize: 12, height: 38, padding: '0 14px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#dc2626', cursor: 'pointer' }}>Löschen</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
export default function Tenants() {
  const { user, subscription, profile } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const limit = getTenantLimit(subscription);
  const { isMobile } = useBreakpoint();

  const [tenants, setTenants]         = useState([]);
  const [properties, setProperties]   = useState([]);
  const [units, setUnits]             = useState([]);
  const [payments, setPayments]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState('');
  const [filterProp, setFilterProp]   = useState('');
  const [filterStatus, setFilterStatus] = useState(null);
  const [inviting, setInviting]       = useState(null);
  const [marking, setMarking]         = useState({});

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth() + 1;
    const start = `${year}-${String(month).padStart(2, '0')}-01`;
    const end   = new Date(year, month, 0).toISOString().split('T')[0];

    const [{ data: t }, { data: p }, { data: u }, { data: pay }, { data: rem }] = await Promise.all([
      supabase.from('tenants').select('*').eq('owner_id', user.id).order('last_name'),
      supabase.from('properties').select('id, name').eq('owner_id', user.id).order('name'),
      supabase.from('units').select('id, property_id, unit_number, floor, rooms').eq('owner_id', user.id).order('unit_number'),
      supabase.from('rent_payments').select('*').eq('owner_id', user.id).gte('due_date', start).lte('due_date', end),
    ]);
    setTenants(t || []);
    setProperties(p || []);
    setUnits(u || []);
    setPayments(pay || []);
    setLoading(false);
  }

  function getPaymentForTenant(tenantId) {
    return payments.find(p => p.tenant_id === tenantId) || null;
  }


  async function handleMarkPaid(payment, tenant) {
    if (!payment) {
      // No payment record yet — create one
      const rent = tenant.monthly_rent || 0;
      if (!rent) { addToast('Kaltmiete fehlt — klicke "Bearb." um sie einzutragen.', 'error'); return; }
      const now   = new Date();
      const year  = now.getFullYear();
      const month = now.getMonth() + 1;
      const due   = calcDueDate(year, month, tenant.rent_due_day);
      const { error } = await supabase.from('rent_payments').insert({
        owner_id: user.id, tenant_id: tenant.id, property_id: tenant.property_id,
        amount_due: rent, amount_paid: rent, due_date: due,
        paid_date: now.toISOString().split('T')[0], status: 'paid',
      });
      if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    } else {
      setMarking(m => ({ ...m, [payment.id]: true }));
      const { error } = await supabase.from('rent_payments')
        .update({ status: 'paid', amount_paid: payment.amount_due, paid_date: new Date().toISOString().split('T')[0] })
        .eq('id', payment.id).eq('owner_id', user.id);
      setMarking(m => ({ ...m, [payment.id]: false }));
      if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    }
    // Auto-create buchung (Einnahme)
    const paidAmount = tenant.monthly_rent || payment?.amount_due || 0;
    if (paidAmount) {
      await supabase.from('buchungen').insert({
        owner_id: user.id, typ: 'einnahme', kategorie: 'Miete',
        beschreibung: `Miete ${tenant.first_name} ${tenant.last_name}`,
        betrag: paidAmount, belegdatum: new Date().toISOString().split('T')[0],
        property_id: tenant.property_id || null, status: 'bezahlt',
        als_bezahlt: true, cashflowrelevant: true,
      });
    }
    await fetchAll();
    addToast('Zahlung als erhalten markiert.', 'success');
  }

  async function handleMarkOpen(payment) {
    if (!payment) return;
    setMarking(m => ({ ...m, [payment.id]: true }));
    const { error } = await supabase.from('rent_payments')
      .update({ status: 'pending', amount_paid: 0, paid_date: null })
      .eq('id', payment.id).eq('owner_id', user.id);
    setMarking(m => ({ ...m, [payment.id]: false }));
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    await fetchAll();
    addToast('Zahlung als offen markiert.', 'success');
  }

  function handleSendReminder(payment, level, tenant) {
    const amount = payment?.amount_due || tenant?.monthly_rent || 0;
    const now = new Date();
    const monat = now.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const name = `${tenant.first_name} ${tenant.last_name}`;
    const betrag = amount ? `${Number(amount).toLocaleString('de-DE')} €` : '–';

    navigate('/nachrichten', {
      state: {
        prefill: {
          type: 'reminder',
          tenantId: tenant.id,
          tenantName: name,
          tenantEmail: tenant.email || '',
          betreff: 'Offene Miete',
          inhalt: `Hallo ${name},\n\nfür die Miete in Höhe von ${betrag} (${monat}) besteht aktuell ein offener Betrag.\nBitte überweise diesen zeitnah.\n\nMit freundlichen Grüßen`,
        },
      },
    });
  }

  function propName(id) {
    return properties.find(p => p.id === id)?.name || null;
  }

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true);

    const payload = {
      first_name:   form.first_name,
      last_name:    form.last_name,
      email:        form.email,
      phone:        form.phone        || null,
      iban:         form.iban         || null,
      lease_start:  form.lease_start  || null,
      lease_end:    form.lease_end    || null,
      monthly_rent: form.monthly_rent !== '' ? Number(form.monthly_rent) : null,
      rent_due_day: form.rent_due_day || '1',
      property_id:  form.property_id  || null,
      unit_id:      form.unit_id      || null,
      owner_id:     user.id,
    };
    const { error } = editing
      ? await supabase.from('tenants').update(payload).eq('id', editing).eq('owner_id', user.id)
      : await supabase.from('tenants').insert(payload);
    if (error) {
      addToast('Fehler: ' + error.message, 'error');
      setSaving(false);
      return;
    }
    await fetchAll();
    setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setSaving(false);
    addToast(editing ? 'Mieter aktualisiert.' : 'Mieter hinzugefügt.', 'success');
  }

  async function handleDelete(id) {
    if (!window.confirm('Mieter wirklich löschen?')) return;
    await supabase.from('tenants').delete().eq('id', id).eq('owner_id', user.id);
    setTenants(t => t.filter(x => x.id !== id));
    addToast('Mieter gelöscht.', 'success');
  }

  function openEdit(t) {
    setEditing(t.id);
    setForm({ first_name: t.first_name || '', last_name: t.last_name || '', email: t.email || '', phone: t.phone || '', iban: t.iban || '', lease_start: t.lease_start || '', lease_end: t.lease_end || '', monthly_rent: t.monthly_rent || '', rent_due_day: t.rent_due_day || '1', property_id: t.property_id || '', unit: t.unit || '', unit_id: t.unit_id || '' });
    setShowForm(true);
  }

  async function inviteTenant(tenant) {
    if (!tenant.email) { addToast('Mieter hat keine E-Mail-Adresse.', 'error'); return; }
    if (tenant.portal_enabled) { addToast('Mieterportal bereits aktiv.', 'info'); return; }
    setInviting(tenant.id);
    try {
      const res  = await fetch('/api/invite-tenant', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId: tenant.id, ownerId: user.id }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler');
      if (data.alreadyActive) {
        addToast('Mieterportal bereits aktiv.', 'info');
      } else {
        // Optimistically update invitation_sent_at so badge shows immediately
        setTenants(prev => prev.map(t =>
          t.id === tenant.id ? { ...t, invitation_sent_at: new Date().toISOString() } : t
        ));
        addToast(`Einladung gesendet an ${tenant.email}`, 'success');
      }
    } catch (err) {
      addToast('Einladung fehlgeschlagen: ' + err.message, 'error');
    }
    setInviting(null);
  }

  function exportCSV() {
    const header = ['Vorname', 'Nachname', 'E-Mail', 'Telefon', 'Einheit', 'Objekt', 'Mietbeginn', 'Mietende', 'Kaltmiete'];
    const rows = tenants.map(t => [t.first_name, t.last_name, t.email, t.phone, t.unit, propName(t.property_id) || '', t.lease_start || '', t.lease_end || '', t.monthly_rent || '']);
    const csv  = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'mieter.csv'; a.click();
    URL.revokeObjectURL(url);
    addToast('CSV exportiert.', 'success');
  }

  const filtered = tenants.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q || (
      `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.unit  || '').toLowerCase().includes(q) ||
      (propName(t.property_id) || '').toLowerCase().includes(q)
    );
    const matchProp = !filterProp || t.property_id === filterProp;
    const ls = leaseStatus(t.lease_start, t.lease_end);
    const matchStatus = !filterStatus || (
      filterStatus === 'leer' ? !t.lease_start : ls?.label === filterStatus
    );
    return matchSearch && matchProp && matchStatus;
  });

  /* ── KPI counts ── */
  const countAll        = tenants.length;
  const countVermietet  = tenants.filter(t => leaseStatus(t.lease_start, t.lease_end)?.label === 'Aktiv').length;
  const countGekuendigt = tenants.filter(t => leaseStatus(t.lease_start, t.lease_end)?.label === 'Beendet').length;
  const countReserviert = tenants.filter(t => leaseStatus(t.lease_start, t.lease_end)?.label === 'Zukünftig').length;
  const countLeer       = tenants.filter(t => !t.lease_start).length;
  const countOverdue    = payments.filter(p => p.status === 'overdue').length;

  const KPI_CARDS = [
    { key: null,          label: 'Alle',        value: countAll },
    { key: 'Aktiv',       label: 'Vermietet',   value: countVermietet },
    { key: 'Beendet',     label: 'Gekündigt',   value: countGekuendigt },
    { key: 'leer',        label: 'Leerstehend', value: countLeer },
    { key: 'Zukünftig',   label: 'Reserviert',  value: countReserviert },
  ];

  const pageTitle = countOverdue > 0
    ? `${countOverdue} ${countOverdue === 1 ? 'Miete überfällig' : 'Mieten überfällig'} – jetzt handeln`
    : 'Mietverhältnisse';

  return (
    <div style={{ maxWidth: 1200, color: T.ink }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, gap:12, flexWrap:'wrap' }}>
        <h1 style={{ margin:0, fontSize: isMobile ? 22 : 28, fontWeight:800, letterSpacing:'-0.6px', color: countOverdue > 0 ? '#dc2626' : T.ink }}>
          {pageTitle}
        </h1>
        <div style={{ display:'flex', gap:8 }}>
          {tenants.length > 0 && (
            <button onClick={exportCSV} className="btn-secondary" style={{ fontSize:12, height:38 }}>CSV</button>
          )}
          <button
            onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }}
            className="btn-primary" style={{ fontSize:13, height:38 }}
          >
            + Mieter hinzufügen
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        {/* Search */}
        <div style={{ position:'relative', flex:'1 1 220px', minWidth:180 }}>
          <svg style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text" placeholder="Mieter durchsuchen…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft:36, borderRadius:9, border:`1.5px solid ${T.border}`, width:'100%', boxSizing:'border-box', fontSize:13 }}
          />
        </div>

        {/* Property filter chip */}
        <div style={{ position:'relative' }}>
          <select
            value={filterProp}
            onChange={e => setFilterProp(e.target.value)}
            style={{ appearance:'none', WebkitAppearance:'none', background:'white', border:`1.5px solid ${filterProp ? T.ink : T.border}`, borderRadius:9, padding:'8px 32px 8px 12px', fontSize:13, color: filterProp ? T.ink : T.ink, cursor:'pointer', fontWeight: filterProp ? 700 : 400, outline:'none', height:38 }}
          >
            <option value="">Alle Objekte</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <svg style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {(search || filterProp || filterStatus) && (
          <button onClick={() => { setSearch(''); setFilterProp(''); setFilterStatus(null); }} style={{ background:'none', border:'none', fontSize:13, color:T.ink, cursor:'pointer', fontWeight:600, padding:'0 4px', whiteSpace:'nowrap' }}>
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${KPI_CARDS.length}, 1fr)`, gap:12, marginBottom:28 }}>
        {KPI_CARDS.map(card => {
          const active = filterStatus === card.key;
          return (
            <div
              key={card.label}
              onClick={() => setFilterStatus(active ? null : card.key)}
              style={{ background:'white', border:`2px solid ${active ? T.ink : T.border}`, borderRadius:12, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.15s', boxShadow: active ? `0 0 0 3px e5e5e5` : '0 1px 4px rgba(0,0,0,0.04)' }}
            >
              <div style={{ fontSize:26, fontWeight:800, letterSpacing:'-0.8px', color:'#f59e0b', marginBottom:4 }}>
                {loading ? '–' : card.value}
              </div>
              <div style={{ fontSize:12, fontWeight:600, color: active ? T.ink : T.muted }}>{card.label}</div>
            </div>
          );
        })}
      </div>

      {/* ── Limit banner ── */}
      {!loading && !canCreateTenant(subscription, tenants.length) && (
        <LimitBanner type="tenants" plan={subscription?.plan || 'starter'} current={tenants.length} max={limit} />
      )}

      {/* ── Content ── */}
      {loading ? (
        /* Skeleton */
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          {[1,2,3,4].map(n => (
            <div key={n} style={{ padding: '18px 24px', borderBottom: n < 4 ? `1px solid ${T.border}` : 'none', display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ flex: 2 }}><div className="skeleton" style={{ width: '70%', height: 14, borderRadius: 6, marginBottom: 6 }} /><div className="skeleton" style={{ width: '55%', height: 11, borderRadius: 5 }} /></div>
              <div className="skeleton" style={{ flex: 1.5, height: 13, borderRadius: 5 }} />
              <div className="skeleton" style={{ flex: 1.5, height: 13, borderRadius: 5 }} />
              <div className="skeleton" style={{ flex: 1, height: 13, borderRadius: 5 }} />
              <div className="skeleton" style={{ width: 56, height: 20, borderRadius: 100 }} />
              <div className="skeleton" style={{ width: 90, height: 28, borderRadius: 8, marginLeft: 'auto' }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        search ? (
          <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:16, textAlign:'center', padding:'60px 24px' }}>
            <div style={{ width:56,height:56,background:'#f7f7f7',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Ergebnisse für „{search}"</div>
            <div style={{ fontSize:13,color:T.muted,lineHeight:1.7 }}>Versuche einen anderen Suchbegriff.</div>
          </div>
        ) : (
          <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:16, overflow:'hidden' }}>
            <div style={{ padding:'48px 40px' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
                <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:`1.5px solid ${T.border}` }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Mieter vorhanden</div>
                  <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Lege deine Mieter an und verknüpfe sie mit deinen Immobilien. Verfolge Zahlungen, versende Mahnungen und lade Mieter ins Portal ein.</div>
                  <button onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }} className="btn-primary" style={{ marginTop:20 }}>+ Ersten Mieter hinzufügen</button>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',gap:16 }}>
                {[
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, title:'Zahlungen verfolgen', text:'Behalte Mietzahlungen und offene Beträge pro Mieter im Überblick.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, title:'Mahnungen versenden', text:'Automatisches Mahnwesen mit Stufen 1–3 direkt per E-Mail.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>, title:'Mieterportal', text:'Lade Mieter ins Portal ein — sie sehen Zahlungen und Dokumente selbst.' },
                ].map((f,i)=>(
                  <div key={i} style={{ background:'#f7f7f7',borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
                    <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
                    <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ) : isMobile ? (
        /* Mobile cards */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(t => {
            const pay = getPaymentForTenant(t.id);
            return (
              <TenantCard
                key={t.id}
                tenant={t}
                propName={propName(t.property_id)}
                payment={pay}
                onMarkPaid={handleMarkPaid}
                onMarkOpen={handleMarkOpen}
                onRemind={handleSendReminder}
                onEdit={openEdit}
                onDelete={handleDelete}
                isMarking={pay ? !!marking[pay.id] : false}
              />
            );
          })}
        </div>
      ) : (
        /* Desktop table */
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.8fr 1.3fr 1fr 1.4fr auto',
            padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: '#fafafa',
          }}>
            {['Mieter', 'Objekt', 'Miete / Mo.', 'Dringlichkeit', ''].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
            ))}
          </div>
          {filtered.map((t, i) => {
            const pay  = getPaymentForTenant(t.id);
            return (
              <TenantRow
                key={t.id}
                tenant={t}
                propName={propName(t.property_id)}
                payment={pay}
                onMarkPaid={handleMarkPaid}
                onMarkOpen={handleMarkOpen}
                onRemind={handleSendReminder}
                onEdit={openEdit}
                onDelete={handleDelete}
                isMarking={pay ? !!marking[pay.id] : false}
                last={i === filtered.length - 1}
              />
            );
          })}
        </div>
      )}

      {/* ── Modal ── */}
      {showForm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : 24 }}
          onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>

            <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  {editing ? 'Bearbeiten' : 'Neuer Mieter'}
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px' }}>
                  {editing ? 'Mieter bearbeiten' : 'Mieter anlegen'}
                </h2>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: T.muted, lineHeight: 1, padding: 4 }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '22px 28px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                <Field label="Vorname *">
                  <input type="text" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Nachname *">
                  <input type="text" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                <Field label="Objekt *">
                  <select
                    value={form.property_id}
                    onChange={e => setForm({ ...form, property_id: e.target.value, unit_id: '' })}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="">– Objekt wählen –</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Einheit">
                  {form.property_id ? (
                    <select
                      value={form.unit_id}
                      onChange={e => setForm({ ...form, unit_id: e.target.value })}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">– Einheit wählen –</option>
                      {units
                        .filter(u => u.property_id === form.property_id)
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.unit_number}{u.floor != null ? ` · ${u.floor}. OG` : ''}{u.rooms ? ` · ${u.rooms} Zi.` : ''}
                          </option>
                        ))
                      }
                    </select>
                  ) : (
                    <input type="text" disabled value="" placeholder="Zuerst Objekt wählen" style={{ ...inputStyle, color: '#9ca3af', cursor: 'not-allowed' }} />
                  )}
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                <Field label="E-Mail">
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Telefon">
                  <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <Field label="IBAN">
                <input type="text" value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} placeholder="DE00 0000 0000 0000 0000 00" style={inputStyle} />
              </Field>

              <div style={{ borderTop: `1px solid ${T.border}`, margin: '6px 0 20px', paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: T.muted, marginBottom: 16 }}>Mietvertrag</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                  <Field label="Mietbeginn">
                    <input type="date" value={form.lease_start} onChange={e => setForm({ ...form, lease_start: e.target.value })} style={inputStyle} />
                  </Field>
                  <Field label="Mietende">
                    <input type="date" value={form.lease_end} onChange={e => setForm({ ...form, lease_end: e.target.value })} style={inputStyle} />
                  </Field>
                  <Field label="Miete / Monat (€)">
                    <input type="number" min="0" step="0.01" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} style={inputStyle} />
                  </Field>
                  <Field label="Fälligkeit">
                    <select value={form.rent_due_day} onChange={e => setForm({ ...form, rent_due_day: e.target.value })} style={inputStyle}>
                      {['1','3','5','10','15','20','25','28'].map(d => (
                        <option key={d} value={d}>{d}. des Monats</option>
                      ))}
                      <option value="end_of_month">Ende des Monats</option>
                    </select>
                  </Field>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} className="btn-primary" style={{ flex: 1, height: 44, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary" style={{ height: 44, fontSize: 14, padding: '0 20px' }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
